<script setup>
/**
 * 六合彩（liuhe）界面还原预览页 —— 对应旧版 chunk web/28.4dd884482bedd058a2bd.js。
 *
 * 旧版该页的玩法分类为「特码 / 特肖 / 特大小 / 特单双 / 和数」，号码按红蓝绿三波着色
 * （旧版样式类 lottery-ball-red / -blue / -green、number-da / -xiao / -dan / -shuang）。
 * 后端 games/ 目录下没有 liuhe 游戏，因此本页不接任何接口，全部为占位数据。
 */
import { computed, ref } from 'vue'
import GamePreviewShell from '../components/GamePreviewShell.vue'

// 香港六合彩标准波色分组：红 17 个、蓝 16 个、绿 16 个，合计 49。
const redNumbers = [1, 2, 7, 8, 12, 13, 18, 19, 23, 24, 29, 30, 34, 35, 40, 45, 46]
const blueNumbers = [3, 4, 9, 10, 14, 15, 20, 25, 26, 31, 36, 37, 41, 42, 47, 48]
const zodiacs = ['鼠', '牛', '虎', '兔', '龙', '蛇', '马', '羊', '猴', '鸡', '狗', '猪']

const types = [
  { key: 'tema', title: '特码' },
  { key: 'texiao', title: '特肖' },
  { key: 'tedx', title: '特大小' },
  { key: 'teds', title: '特单双' },
  { key: 'heshu', title: '和数' },
]
// 占位赔率，仅用于界面展示；接后端后应改为读取 /home/game/odds。
const propPlays = {
  tedx: [['大', '1.950'], ['小', '1.950']],
  teds: [['单', '1.950'], ['双', '1.950']],
  heshu: [['和大', '1.950'], ['和小', '1.950'], ['和单', '1.950'], ['和双', '1.950']],
}
const placeholderLottery = {
  period: '2026090',
  normals: [7, 23, 31, 16, 44, 2],
  special: 38,
}
const placeholderHistory = [
  { no: '2026089', normals: [12, 3, 21, 40, 9, 35], special: 17 },
  { no: '2026088', normals: [45, 6, 26, 14, 33, 1], special: 20 },
  { no: '2026087', normals: [8, 39, 15, 47, 22, 30], special: 5 },
]

const activeType = ref('tema')
const selected = ref(new Set())

function ballColor(number) {
  if (redNumbers.includes(Number(number))) return 'ball-red'
  if (blueNumbers.includes(Number(number))) return 'ball-blue'
  return 'ball-green'
}
function zodiacOf(number) {
  // 占位换算：真实生肖需按当年鼠年起始号推算，这里只保证展示稳定。
  return zodiacs[(Number(number) - 1) % 12]
}
const numbers = computed(() => Array.from({ length: 49 }, (_, index) => index + 1))
const specialSummary = computed(() => {
  const special = placeholderLottery.special
  const digitSum = String(special).split('').reduce((sum, digit) => sum + Number(digit), 0)
  return `特码 ${special} ${special >= 25 ? '大' : '小'}${special % 2 ? '单' : '双'} 和${digitSum}`
})

function toggle(key) {
  const next = new Set(selected.value)
  next.has(key) ? next.delete(key) : next.add(key)
  selected.value = next
}
function reset() { selected.value = new Set() }
</script>

<template>
  <GamePreviewShell
    title="六合彩"
    :period="placeholderLottery.period"
    :countdown="138"
    :total-bet="0"
    :selected-count="selected.size"
    @reset="reset"
  >
    <template #lottery-summary><span class="summary">{{ specialSummary }}</span></template>

    <template #lottery>
      <i v-for="(ball, index) in placeholderLottery.normals" :key="`n-${index}`" :class="['lottery-ball', ballColor(ball)]">{{ String(ball).padStart(2, '0') }}</i>
      <b class="plus">+</b>
      <i :class="['lottery-ball', ballColor(placeholderLottery.special)]">{{ String(placeholderLottery.special).padStart(2, '0') }}</i>
    </template>

    <template #history>
      <article v-for="row in placeholderHistory" :key="row.no" class="history-row">
        <span>{{ row.no }}</span>
        <div>
          <i v-for="(ball, index) in row.normals" :key="`${row.no}-${index}`" :class="ballColor(ball)">{{ String(ball).padStart(2, '0') }}</i>
          <b>+</b>
          <i :class="ballColor(row.special)">{{ String(row.special).padStart(2, '0') }}</i>
        </div>
      </article>
    </template>

    <template #types>
      <button
        v-for="type in types"
        :key="type.key"
        type="button"
        :class="['bet-type-item', { 'bet-type-active': activeType === type.key }]"
        @click="activeType = type.key"
      >{{ type.title }}</button>
    </template>

    <template #board>
      <div v-if="activeType === 'tema'" class="board-grid grid-7">
        <button
          v-for="number in numbers"
          :key="number"
          type="button"
          :class="['board-item', { selected: selected.has(`tema-${number}`) }]"
          @click="toggle(`tema-${number}`)"
        >
          <i :class="['board-ball', ballColor(number)]">{{ String(number).padStart(2, '0') }}</i>
          <small>{{ zodiacOf(number) }}</small>
        </button>
      </div>

      <div v-else-if="activeType === 'texiao'" class="board-grid grid-4">
        <button
          v-for="zodiac in zodiacs"
          :key="zodiac"
          type="button"
          :class="['board-item', 'text-item', { selected: selected.has(`xiao-${zodiac}`) }]"
          @click="toggle(`xiao-${zodiac}`)"
        >
          <strong>{{ zodiac }}</strong>
          <small class="odds">11.500</small>
        </button>
      </div>

      <div v-else class="board-grid grid-4">
        <button
          v-for="[label, odds] in propPlays[activeType]"
          :key="label"
          type="button"
          :class="['board-item', 'text-item', { selected: selected.has(`${activeType}-${label}`) }]"
          @click="toggle(`${activeType}-${label}`)"
        >
          <strong>{{ label }}</strong>
          <small class="odds">{{ odds }}</small>
        </button>
      </div>
    </template>
  </GamePreviewShell>
</template>

<style scoped>
.summary{color:#111;font-size:12px}
.lottery-ball{display:inline-grid;width:30px;height:30px;place-items:center;border-radius:50%;color:#fff;font-size:14px;font-style:normal}
.plus{color:#999}
.ball-red{background:#e8353d}
.ball-blue{background:#2f7fd6}
.ball-green{background:#1f9d55}
.history-row{display:grid;grid-template-columns:74px 1fr;min-height:32px;align-items:center;padding:0 11px;border-bottom:1px solid #e5e5e5}
.history-row>div{display:flex;align-items:center;gap:4px}
.history-row i{display:inline-grid;width:22px;height:22px;place-items:center;border-radius:50%;color:#fff;font-size:11px;font-style:normal}
.history-row b{color:#999}
.bet-type-item{flex:0 0 auto;border:0;border-right:1px solid #eee;background:#fff;color:#444;padding:9px 16px;font-size:13px;white-space:nowrap}
.bet-type-active{background:#f04141;color:#fff}
.board-grid{display:grid;gap:8px}
.grid-7{grid-template-columns:repeat(7,1fr)}
.grid-4{grid-template-columns:repeat(4,1fr)}
.board-item{display:grid;place-items:center;gap:3px;border:1px solid #77c697;background:#389b61;color:#fff;padding:6px 2px}
.board-item.selected{border-color:#ffe372;background:#24804e;box-shadow:inset 0 0 0 1px #ffe372}
.board-ball{display:inline-grid;width:26px;height:26px;place-items:center;border-radius:50%;color:#fff;font-size:12px;font-style:normal}
.board-item small{color:#d5efdf;font-size:10px}
.text-item{min-height:48px}
.text-item strong{font-size:15px;font-weight:600}
.odds{color:#ffe561!important;font-size:11px}
</style>
